import React from 'react';
import moment from 'moment-timezone';
import CalendarAtom from '../Calendar/CalendarAtom';
import PrimeReactSingleSelect, { OptionType } from '../single-select-dropdown/single-select-dropdown';
import { useTranslation } from 'localization';
import './TimePickerAtom.scss';

export interface TimePickerAtomProps {
  /** "HH:mm" */
  value: string;
  onChange: (value: string) => void;
  /** IANA zone */
  zone: string;
  zoneOptions?: string[];
  onZoneChange: (zone: string) => void;
  /** anchor date for time-only calendar */
  baseDate?: Date | null;
  errors?: string[];
  className?: string;
  /** name attribute for Formik blur handling */
  name?: string;
  onBlur?: React.FocusEventHandler<any>;
}

/** Build a JS Date whose *displayed* local‐time corresponds to hh:mm in `zone` and anchored to `baseDate`. */
const toDateInZone = (
  hhmm: string,
  zone: string,
  baseDate?: Date | null
): Date | null => {
  if (!hhmm) return null;
  // Choose date-source: either the provided baseDate or today in that zone
  const source = baseDate
    ? moment(baseDate).tz(zone).format('YYYY-MM-DD')
    : moment().tz(zone).format('YYYY-MM-DD');
  const m = moment.tz(`${source} ${hhmm}`, 'YYYY-MM-DD HH:mm', zone);
  return m.isValid() ? m.toDate() : null;
};

/** Given a JS Date, grab its hh:mm *in the chosen zone*. */
const toHHMMInZone = (d: Date | null, zone: string): string => {
  if (!d) return '';
  return moment(d).tz(zone).format('HH:mm');
};

const TimePickerAtom: React.FC<TimePickerAtomProps> = ({
  value,
  onChange,
  zone,
  zoneOptions,
  baseDate,
  onZoneChange,
  errors = [],
  className = '',
  name,
  onBlur,
}) => {
  console.log('[TimePickerAtom] Rendered with:', { value, zone, baseDate, errors, className });
  const { t } = useTranslation();
  const hasError = errors.length > 0;

  const rawZones = zoneOptions ?? moment.tz.names();
  const zoneOptionItems: OptionType[] = rawZones.map(z => ({ name: z, code: z }));
  const selectedZone = zoneOptionItems.find(o => o.code === zone) || null;

  const handleTimeChange = (date: Date | Date[] | null) => {
    // For time-only mode, we always get a single Date, not an array
    const singleDate = Array.isArray(date) ? date[0] || null : date;
    const hhmm = toHHMMInZone(singleDate, zone);
    onChange(hhmm);
  };

  const handleZoneChange = (e: any) => {
    const newZone = e.value?.code || '';
    // When timezone changes, we want to keep the same wall clock time
    // So we just update the zone without converting the time
    onZoneChange(newZone);
  };

  const inputId = name || 'time-picker-input';

  return (
    <div className={`time-picker ${className}`}>
      <label className="time-picker__label" htmlFor={inputId}>
        {t('scheduleEditor.timeLabel')}
      </label>

      <div className="time-picker__control">
        <CalendarAtom
          name={name}
          value={toDateInZone(value, zone, baseDate)}
          onDateChange={handleTimeChange}
          onBlur={onBlur}
          timeOnly
          hourFormat="24"
          className={`time-picker__input${hasError ? ' time-picker__input--error' : ''}`}
          showIcon
          icon="pi pi-clock"
          inputId={inputId}
        />

        <PrimeReactSingleSelect
          value={selectedZone}
          options={zoneOptionItems}
          onChange={handleZoneChange}
          placeholder={t('scheduleEditor.timezonePlaceholder')}
          optionLabel="name"
          filter
          className={`time-picker__select${hasError ? ' time-picker__select--error' : ''}`}
        />
      </div>

      {hasError && (
        <ul className="time-picker__errors">
          {errors.map(err => <li key={err}>{err}</li>)}
        </ul>
      )}
    </div>
  );
};

export default TimePickerAtom;